import { useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import type { Unidade } from "types/pessoa";

interface UnidadeSelectorProps {
  rotulo: string;
  descricao: string;
  tooltip: string;
  unidades: Unidade[];
  carregando: boolean;
  falhou: boolean;
  onTentarNovamente: () => void;
  value: Unidade | null;
  onChange: (unidade: Unidade | null) => void;
  error?: string;
}

const descrever = (unidade: Unidade) => `${unidade.nome} (${unidade.prefixo})`;

/** Combobox da unidade validadora: busca local por nome ou prefixo na lista da base de pessoas. */
export function UnidadeSelector({
  rotulo,
  descricao,
  tooltip,
  unidades,
  carregando,
  falhou,
  onTentarNovamente,
  value,
  onChange,
  error,
}: UnidadeSelectorProps) {
  const [termo, setTermo] = useState("");
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fecharAoClicarFora = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", fecharAoClicarFora);
    return () => document.removeEventListener("mousedown", fecharAoClicarFora);
  }, []);

  const opcoes = useMemo(() => {
    const busca = termo.trim().toLowerCase();
    return busca
      ? unidades.filter((u) => u.nome.toLowerCase().includes(busca) || String(u.prefixo).includes(busca))
      : unidades;
  }, [unidades, termo]);

  const selecionar = (unidade: Unidade) => {
    onChange(unidade);
    setTermo("");
    setAberto(false);
  };

  const trocar = () => {
    onChange(null);
    setAberto(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const tooltipId = "tooltip-unidade-validadora";

  return (
    <div ref={containerRef}>
      <label htmlFor="unidade-validadora" className="flex items-center gap-1.5 text-gray-bb-1 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">
        {rotulo}
        <span
          data-tooltip-id={tooltipId}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-bb text-white-bb text-xs font-bold cursor-help shrink-0"
          aria-label="Ajuda"
        >
          ?
        </span>
        <Tooltip id={tooltipId} place="top" className="max-w-xs sm:max-w-sm z-50 whitespace-pre-line" content={tooltip} />
      </label>
      <p className="text-sm text-gray-bb-2 mb-2">{descricao}</p>

      {value ? (
        <div className={`w-full border rounded-lg p-3 flex items-center justify-between ${error ? "border-red-500" : "border-gray-bb-4"}`}>
          <span className="font-medium text-gray-bb-1">{descrever(value)}</span>
          <button type="button" onClick={trocar} className="text-blue-bb font-semibold text-sm ml-2">
            Alterar
          </button>
        </div>
      ) : (
        <input
          id="unidade-validadora"
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={aberto}
          aria-controls="unidades-lista"
          aria-autocomplete="list"
          value={termo}
          disabled={carregando || falhou}
          onChange={(e) => {
            setTermo(e.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          placeholder={carregando ? "Carregando unidades..." : "Digite o nome ou o prefixo"}
          className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all disabled:bg-gray-100 ${
            error ? "border-red-500" : "border-gray-bb-4"
          }`}
        />
      )}

      {falhou && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          Não foi possível carregar as unidades.{" "}
          <button type="button" onClick={onTentarNovamente} className="underline font-semibold">
            Tentar novamente
          </button>
        </p>
      )}

      {aberto && !value && !carregando && !falhou && (
        <div id="unidades-lista" role="listbox" className="mt-1 border border-gray-bb-4 rounded-lg max-h-72 overflow-y-auto bg-white shadow-lg">
          {opcoes.length === 0 ? (
            <div className="p-3 text-gray-bb-2 text-sm">Nenhuma unidade encontrada</div>
          ) : (
            opcoes.map((unidade) => (
              <button
                key={unidade.prefixo}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => selecionar(unidade)}
                className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-bb-4 last:border-b-0 text-sm sm:text-base text-gray-bb-1"
              >
                {descrever(unidade)}
              </button>
            ))
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
